import { CommonModule } from '@angular/common';
import { Component, Inject, OnDestroy, Optional } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { of, Subject, Subscription } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, finalize, switchMap, takeUntil } from 'rxjs/operators';
import { UserDashboardService } from '../../services/user-dashboard.service';
import { CreateProjectService } from '../../services/create-project.service';

const DEPT_PAGE_SIZE = 25;

interface MemberResult {
  id: string;
  name: string;
  email: string;
  department: string;
  raw: any;
}

interface DepartmentOption {
  id: string;
  name: string;
  count: number;
}

interface SavedDeptGroup {
  deptname: string;
  flag: boolean;
  dept_id?: string;
  deptId?: string;
  selected_members?: any[];
  selectedMembers?: any[];
}

interface CustomDepartmentDialogData {
  deptId?: string;
  name?: string;
  members?: any[];
  customSelectedDept?: SavedDeptGroup[];
  existingDepartmentNames?: string[];
}

@Component({
  selector: 'app-open-add-custom-department-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MatIconModule],
  templateUrl: './open-add-custom-department-dialog.component.html',
  styleUrls: ['./open-add-custom-department-dialog.component.css']
})
export class OpenAddCustomDepartmentDialogComponent implements OnDestroy {
  readonly maxDepartmentNameLength = 100;
  searchText = '';
  customName = '';
  wasDepartmentNameTrimmed = false;
  results: MemberResult[] = [];
  selectedMembers: MemberResult[] = [];
  isSearching = false;
  hasSearched = false;
  isSaving = false;
  isEdit = false;

  searchMode: 'members' | 'departments' = 'members';

  deptSearchText = '';
  deptResults: DepartmentOption[] = [];
  isDeptLoading = false;
  isDeptLoadingMore = false;
  allDeptsLoaded = false;

  selectedDepartments: DepartmentOption[] = [];

  private searchTimer: any;
  private readonly deptId: string | null = null;
  private readonly savedGroups: SavedDeptGroup[] = [];
  private originalSignature = '';
  private originalSignatureCaptured = false;
  private originalName = '';
  private deptPage = 1;
  private deptRequest?: Subscription;
  private readonly deptScroll$ = new Subject<Event>();
  private readonly deptSearch$ = new Subject<string>();
  private readonly destroy$ = new Subject<void>();
  private readonly knownDeptsById = new Map<string, DepartmentOption>();
  private readonly selectedDeptDetails = new Map<string, DepartmentOption>();
  private readonly unresolvedSavedGroupNames = new Set<string>();
  private readonly selectedDeptIds = new Set<string>();
  private readonly deptMemberCache = new Map<string, MemberResult[]>();
  private readonly providedDeptMembers = new Map<string, MemberResult[]>();
  private readonly manualMemberIds = new Set<string>();
  private readonly loadingDeptIds = new Set<string>();
  private readonly expandedDeptIds = new Set<string>();
  private readonly existingDepartmentNames = new Set<string>();

  private readonly selectedMemberKeys = new Set<string>();
  private readonly selectedCountCache = new Map<string, number>();

  constructor(
    private readonly dialogRef: MatDialogRef<OpenAddCustomDepartmentDialogComponent>,
    private readonly userDashboardService: UserDashboardService,
    private readonly createProjectService: CreateProjectService,
    @Optional() @Inject(MAT_DIALOG_DATA) data: CustomDepartmentDialogData | null
  ) {
    this.deptId = data?.deptId ?? null;
    this.isEdit = !!this.deptId;
    this.savedGroups = data?.customSelectedDept ?? [];
    (data?.existingDepartmentNames ?? []).forEach(name => {
      const normalized = this.normalizeDepartmentName(name);
      if (normalized) { this.existingDepartmentNames.add(normalized); }
    });
    if (data?.name) { this.customName = data.name; this.originalName = data.name; }
    if (this.isEdit) {
      this.existingDepartmentNames.delete(this.normalizeDepartmentName(this.customName));
    }
    if (data?.members?.length) {
      this.selectedMembers = data.members.map(m => this.toResult(m));
      this.rebuildSelectedMemberKeys();
      this.seedManualMembers();
    }
    this.savedGroups
      .filter(g => g.flag && !!g.deptname)
      .forEach(g => this.unresolvedSavedGroupNames.add(this.normalizeDepartmentName(g.deptname)));

    this.deptScroll$.pipe(debounceTime(300), takeUntil(this.destroy$))
      .subscribe(event => this.handleDeptScroll(event));
    // this.deptSearch$.pipe(debounceTime(300), distinctUntilChanged(), takeUntil(this.destroy$))
    //   .subscribe(() => this.reloadDeptSearch());

    this.deptSearch$.pipe(
      debounceTime(400),
      distinctUntilChanged(),
      switchMap(searchTerm => {
        this.deptPage = 1;
        this.allDeptsLoaded = false;
        this.isDeptLoading = true;
        return this.createProjectService.fetchDepartments(null, 1, 'asc', searchTerm).pipe(
          catchError(error => {
            console.info('Load departments failed', error);
            return of({ payload: [] });
          })
        );
      }),
  takeUntil(this.destroy$)
).subscribe((resp: any) => {
  this.isDeptLoading = false;
  this.deptResults = [];
  const payload: any[] = resp?.payload ?? [];
  if (payload.length < DEPT_PAGE_SIZE) { this.allDeptsLoaded = true; }
  this.ingestDeptPage(payload);
  this.resolveSavedDepartments();
  this.captureOriginalSignatureIfReady();
  this.refreshSelectedDepartments();
});

    this.loadDeptPage(1);
    // Render the saved selection straight from the dialog data - it does not wait on
    // the department list request.
    this.resolveSavedDepartments();
    this.captureOriginalSignatureIfReady();
    this.refreshSelectedDepartments();
  }

  ngOnDestroy(): void {
    this.deptRequest?.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private memberKey(m: MemberResult): string {
    const workdayId = this.workdayIdOf(m);
    if (workdayId) { return `id:${workdayId}`; }
    if (m.id) { return `id:${m.id}`; }
   const email = (m.email || '').toLowerCase();
    return email ? `email:${email}` : '';
  }

  private rebuildSelectedMemberKeys(): void {
    this.selectedMemberKeys.clear();
    this.selectedMembers.forEach(m => {
      const key = this.memberKey(m);
      if (key) { this.selectedMemberKeys.add(key); }
    });
    this.selectedCountCache.clear();
  }

  private isSameMember(a: MemberResult, b: MemberResult): boolean {
    if (a.id && b.id && a.id === b.id) { return true; }
    const emailA = (a.email || '').toLowerCase();
    const emailB = (b.email || '').toLowerCase();
    return !!emailA && emailA === emailB;
  }

  isSelected(item: MemberResult): boolean {
    const key = this.memberKey(item);
    return !!key && this.selectedMemberKeys.has(key);
  }

  toggleMember(item: MemberResult): void {
    if (this.isSelected(item)) {
      this.selectedMembers = this.selectedMembers.filter(s => !this.isSameMember(s, item));
      this.manualMemberIds.delete(item.id);
    } else {
      this.selectedMembers = [...this.selectedMembers, item];
      this.manualMemberIds.add(item.id);
    }
    this.rebuildSelectedMemberKeys();
    this.reconcileDeptSelection();
    this.refreshSelectedDepartments();
  }

  removeSelected(item: MemberResult): void {
    this.selectedMembers = this.selectedMembers.filter(s => !this.isSameMember(s, item));
    this.manualMemberIds.delete(item.id);
    this.rebuildSelectedMemberKeys();
    this.reconcileDeptSelection();
    this.refreshSelectedDepartments();
  }

  clearSearch(): void {
    this.searchText = '';
    this.results = [];
    this.hasSearched = false;
  }

  onSearchInput(): void {
    if (this.searchTimer) { clearTimeout(this.searchTimer); }
    const term = this.searchText.trim();
    if (!term) {
      this.results = [];
      this.isSearching = false;
      this.hasSearched = false;
      return;
    }
    this.isSearching = true;
    this.searchTimer = setTimeout(() => this.runSearch(term), 400);
  }

  private async runSearch(term: string): Promise<void> {
    try {
      const res = await this.userDashboardService.searchAllUsersDetails(term);
      const list: any[] = res?.payload ?? [];
      this.results = list
        .map((m: any) => this.toResult(m))
        .filter((r: MemberResult) => !!r.name || !!r.email);
    } catch (error) {
      console.info('Team member search failed', error);
      this.results = [];
    } finally {
      this.isSearching = false;
      this.hasSearched = true;
    }
  }

  private toResult(m: any): MemberResult {
    return {
      id: String(m?.tm_workday_id ?? m?.user_guid ?? m?.user_id ?? m?.userId ?? m?.tm_id ?? ''),
      name: String(m?.tm_name ?? m?.name ?? '').trim(),
      email: String(m?.email_address ?? m?.tm_email ?? m?.email ?? '').trim(),
      department: String(m?.department ?? m?.department_name ?? '').trim(),
      raw: m
    };
  }

  setSearchMode(mode: 'members' | 'departments'): void {
    this.searchMode = mode;
  }

  onDepartmentNameChange(value: string): void {
    if ((value || '').length > this.maxDepartmentNameLength) {
      this.customName = value.slice(0, this.maxDepartmentNameLength);
      this.wasDepartmentNameTrimmed = true;
      return;
    }
    this.customName = value;
    this.wasDepartmentNameTrimmed = false;
  }

  onDepartmentNameKeydown(event: KeyboardEvent): void {
    if (this.customName.length < this.maxDepartmentNameLength) {
      this.wasDepartmentNameTrimmed = false;
      return;
    }

    const navigationKeys = new Set([
      'Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'
    ]);
    if (navigationKeys.has(event.key) || event.ctrlKey || event.metaKey || event.altKey) {
      return;
    }

    this.wasDepartmentNameTrimmed = true;
  }

  onDepartmentNamePaste(event: ClipboardEvent): void {
    const pasted = event.clipboardData?.getData('text') ?? '';
    if (!pasted) { return; }

    const target = event.target as HTMLInputElement | null;
    const currentValue = target?.value ?? this.customName;
    const selectionStart = target?.selectionStart ?? currentValue.length;
    const selectionEnd = target?.selectionEnd ?? currentValue.length;
    const replacedLength = Math.max(0, selectionEnd - selectionStart);
    const projectedLength = currentValue.length - replacedLength + pasted.length;

    if (projectedLength > this.maxDepartmentNameLength) {
      this.wasDepartmentNameTrimmed = true;
    }
  }

  private loadDeptPage(page: number): void {
    if (this.isDeptLoadingMore) { return; }
    const isFirstPage = page === 1;
    this.isDeptLoadingMore = true;
    this.isDeptLoading = isFirstPage && this.deptResults.length === 0;

    this.deptRequest = this.createProjectService.fetchDepartments(null, page, 'asc', this.deptSearchText)
      .pipe(finalize(() => {
        this.isDeptLoadingMore = false;
        this.isDeptLoading = false;
      }))
      .subscribe({
        next: (resp: any) => {
          const payload: any[] = resp?.payload ?? [];
          if (payload.length < DEPT_PAGE_SIZE) { this.allDeptsLoaded = true; }
          this.ingestDeptPage(payload);
          this.resolveSavedDepartments();
          this.captureOriginalSignatureIfReady();
          this.refreshSelectedDepartments();
        },
        error: (error: any) => {
          console.info('Load departments failed', error);
          if (isFirstPage) { this.deptResults = []; }
        }
      });
  }

  private ingestDeptPage(payload: any[]): void {
    const existingIds = new Set(this.deptResults.map(d => d.id));
    const mapped = (payload ?? [])
      .map((d: any) => this.toDeptOption(d))
      .filter((d: DepartmentOption) => !!d.id && !!d.name);

    mapped.forEach(d => {
      this.knownDeptsById.set(d.id, d);
      this.rebindSavedDepartment(d);
    });

    const newForPage = mapped.filter(d => !existingIds.has(d.id));
    if (newForPage.length) {
      this.deptResults = [...this.deptResults, ...newForPage];
    }
  }

  private toDeptOption(d: any): DepartmentOption {
    return {
      id: String(d?.dept_id ?? ''),
      name: String(d?.department_name ?? '').trim(),
      count: Number(d?.team_member_count ?? 0)
    };
  }

  private placeholderDeptId(name: string): string {
    return `saved:${this.normalizeDepartmentName(name)}`;
  }

  private isPlaceholderDeptId(deptId: string): boolean {
    return deptId.startsWith('saved:');
  }

  // A saved department whose id the API does not return starts out with a placeholder
  // id, so opening the dialog needs no extra requests. As soon as the real department
  // turns up - while browsing/searching the list, or when the user opens it - its
  // selection state is moved over to the real id.
  private rebindSavedDepartment(real: DepartmentOption): void {
    const placeholderId = this.placeholderDeptId(real.name);
    if (placeholderId === real.id || !this.selectedDeptIds.has(placeholderId)) { return; }

    this.selectedDeptIds.delete(placeholderId);
    this.selectedDeptIds.add(real.id);

    const members = this.providedDeptMembers.get(placeholderId);
    if (members) {
      this.providedDeptMembers.delete(placeholderId);
      this.providedDeptMembers.set(real.id, members);
    }

    this.selectedDeptDetails.delete(placeholderId);
    this.selectedDeptDetails.set(real.id, real);

    if (this.expandedDeptIds.delete(placeholderId)) {
      this.expandedDeptIds.add(real.id);
    }

    this.selectedCountCache.delete(placeholderId);
    this.selectedCountCache.delete(real.id);
  }

  private getCachedDeptMembers(deptId: string): MemberResult[] {
    return this.deptMemberCache.get(deptId) ?? this.providedDeptMembers.get(deptId) ?? [];
  }

  private groupMemberIds(group: SavedDeptGroup): string[] {
    return (group.selected_members ?? group.selectedMembers ?? [])
      .map((entry: any) =>
        entry && typeof entry === 'object'
          ? String(entry.tm_workday_id ?? entry.user_guid ?? entry.user_id ?? entry.userId ?? entry.tm_id ?? '')
          : String(entry))
      .filter((id: string) => !!id);
  }

  private seedManualMembers(): void {
    if (!this.savedGroups.length) {
      this.selectedMembers.forEach(m => this.manualMemberIds.add(m.id));
      return;
    }
    const looseIds = new Set<string>();
    this.savedGroups
      .filter(g => !g.flag)
      .forEach(g => this.groupMemberIds(g).forEach(id => looseIds.add(id)));
    this.selectedMembers.forEach(m => {
      if (looseIds.has(this.workdayIdOf(m)) || looseIds.has(m.id)) {
        this.manualMemberIds.add(m.id);
      }
    });
  }

  // The dialog data already carries everything needed to render the saved selection
  // (department names + their members), so no request is made here. The real dept_id
  // is used when the API provides one, otherwise a placeholder stands in until the
  // department is actually needed.
  private resolveSavedDepartments(): void {
    if (!this.unresolvedSavedGroupNames.size) { return; }

    const byName = new Map<string, DepartmentOption>();
    this.deptResults.forEach(d => byName.set(this.normalizeDepartmentName(d.name), d));

    this.savedGroups
      .filter(g => g.flag && !!g.deptname && this.unresolvedSavedGroupNames.has(this.normalizeDepartmentName(g.deptname)))
      .forEach(g => {
        const normalized = this.normalizeDepartmentName(g.deptname);
        const savedId = String(g.dept_id ?? g.deptId ?? '').trim();
        const dept = (savedId ? { id: savedId, name: g.deptname, count: this.groupMemberIds(g).length } : undefined)
          ?? byName.get(normalized)
          ?? { id: this.placeholderDeptId(g.deptname), name: g.deptname, count: this.groupMemberIds(g).length };
        this.applySavedDepartmentGroup(dept, g);
        this.unresolvedSavedGroupNames.delete(normalized);
      });
  }

  private resolvePlaceholderDept(dept: DepartmentOption, done: (resolved: DepartmentOption | null) => void): void {
    if (this.loadingDeptIds.has(dept.id)) { return; }
    this.loadingDeptIds.add(dept.id);

    const normalized = this.normalizeDepartmentName(dept.name);
    this.createProjectService.fetchDepartments(null, 1, 'asc', dept.name)
      .pipe(finalize(() => this.loadingDeptIds.delete(dept.id)))
      .subscribe({
        next: (resp: any) => {
          const match = (resp?.payload ?? [])
            .map((d: any) => this.toDeptOption(d))
            .find((d: DepartmentOption) => !!d.id && this.normalizeDepartmentName(d.name) === normalized);
          if (!match) { done(null); return; }
          this.knownDeptsById.set(match.id, match);
          this.rebindSavedDepartment(match);
          this.refreshSelectedDepartments();
          done(match);
        },
        error: (error: any) => {
          console.info('Resolve saved department failed', error);
          done(null);
        }
      });
  }

  private applySavedDepartmentGroup(dept: DepartmentOption, g: SavedDeptGroup): boolean {
    const ids = new Set(this.groupMemberIds(g));
    const members = this.selectedMembers.filter(m => ids.has(this.workdayIdOf(m)) || ids.has(m.id));
    if (!members.length) { return false; }
    this.selectedDeptIds.add(dept.id);
    this.providedDeptMembers.set(dept.id, members);
    this.selectedDeptDetails.set(dept.id, dept);
    this.selectedCountCache.delete(dept.id);
    return true;
  }

  private captureOriginalSignatureIfReady(): void {
    if (!this.isEdit || this.originalSignatureCaptured || this.unresolvedSavedGroupNames.size) { return; }
    this.originalSignature = this.selectionSignature();
    this.originalSignatureCaptured = true;
  }

  onDeptSearchInput(): void {
    this.deptSearch$.next(this.deptSearchText.trim());
  }

  private reloadDeptSearch(): void {
    this.deptRequest?.unsubscribe();
    this.deptPage = 1;
    this.allDeptsLoaded = false;
    this.deptResults = [];
    this.loadDeptPage(1);
  }

  onDeptListScroll(event: Event): void {
    if (this.searchMode !== 'departments') { return; }
    this.deptScroll$.next(event);
  }

  private handleDeptScroll(event: Event): void {
    const el = event.target as HTMLElement;
    const threshold = 100;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < threshold;
    if (nearBottom && !this.isDeptLoadingMore && !this.allDeptsLoaded) {
      this.deptPage++;
      this.loadDeptPage(this.deptPage);
    }
  }

  clearDeptSearch(): void {
    this.deptSearchText = '';
    this.reloadDeptSearch();
  }

  private refreshSelectedDepartments(): void {
    const result = new Map<string, DepartmentOption>(this.selectedDeptDetails);
    this.knownDeptsById.forEach((dept, id) => {
      if (!result.has(id) && this.getSelectedMemberCount(dept) > 0) {
        result.set(id, dept);
      }
    });
    this.selectedDepartments = Array.from(result.values());
  }

  isDeptSelected(dept: DepartmentOption): boolean {
    return this.selectedDeptIds.has(dept.id) || this.getSelectedMemberCount(dept) > 0;
  }

  private reconcileDeptSelection(): void {
  for (const deptId of Array.from(this.selectedDeptIds)) {
    const members = this.getCachedDeptMembers(deptId);
    if (!members.length) {
      continue;
    }
    const hasSelected = members.some(m => this.isSelected(m));
    if (!hasSelected) {
      this.selectedDeptIds.delete(deptId);
      this.selectedDeptDetails.delete(deptId);
      this.selectedCountCache.delete(deptId);
      this.expandedDeptIds.delete(deptId);
    }
  }
}

  isDeptLoadingMembers(dept: DepartmentOption): boolean {
    return this.loadingDeptIds.has(dept.id);
  }

  getSelectedMemberCount(dept: DepartmentOption): number {
    const cached = this.selectedCountCache.get(dept.id);
    if (cached !== undefined) { return cached; }

    const members = this.getCachedDeptMembers(dept.id);
    const count = members.length === 0
      ? 0
      : members.reduce((c, m) => (this.isSelected(m) ? c + 1 : c), 0);

    this.selectedCountCache.set(dept.id, count);
    return count;
  }

  getDeptMembers(dept: DepartmentOption): MemberResult[] {
    return this.getCachedDeptMembers(dept.id);
  }

  isDeptExpanded(dept: DepartmentOption): boolean {
    return this.expandedDeptIds.has(dept.id);
  }

  toggleDeptExpand(dept: DepartmentOption): void {
    if (this.expandedDeptIds.has(dept.id)) {
      this.expandedDeptIds.delete(dept.id);
    } else {
      this.expandedDeptIds.add(dept.id);
      this.loadDeptMembers(dept);
    }
  }

  toggleDepartment(dept: DepartmentOption): void {
    if (this.isDeptSelected(dept)) {
      this.deselectDepartment(dept);
    } else {
      this.selectDepartment(dept);
    }
  }

  removeDepartment(dept: DepartmentOption): void {
    this.deselectDepartment(dept);
  }

  private selectDepartment(dept: DepartmentOption): void {
    this.selectedDeptIds.add(dept.id);
    this.selectedDeptDetails.set(dept.id, dept);
    this.selectedCountCache.delete(dept.id);
    this.loadDeptMembers(dept, members => {
      if (this.selectedDeptIds.has(dept.id)) {
        this.mergeDeptMembers(members);
        this.refreshSelectedDepartments();
      }
    });
    this.refreshSelectedDepartments();
  }

  private loadDeptMembers(dept: DepartmentOption, onLoaded?: (members: MemberResult[]) => void): void {
    const cached = this.deptMemberCache.get(dept.id);
    if (cached) {
      if (onLoaded) { onLoaded(cached); }
      return;
    }
    if (this.loadingDeptIds.has(dept.id)) { return; }

    if (this.isPlaceholderDeptId(dept.id)) {
      this.resolvePlaceholderDept(dept, resolved => {
        if (resolved) { this.loadDeptMembers(resolved, onLoaded); }
      });
      return;
    }

    this.loadingDeptIds.add(dept.id);
    this.createProjectService.fetchTeamMembers(dept.id, null)
      .pipe(finalize(() => this.loadingDeptIds.delete(dept.id)))
      .subscribe({
        next: (members: any[]) => {
          const mapped = (members || []).map(m => this.toResult(m));
          this.deptMemberCache.set(dept.id, mapped);
          this.selectedCountCache.delete(dept.id);
          if (onLoaded) { onLoaded(mapped); }
        },
        error: (error: any) => {
          console.info('Load department members failed', error);
        }
      });
  }

  private deselectDepartment(dept: DepartmentOption): void {
    this.selectedDeptIds.delete(dept.id);
    this.selectedDeptDetails.delete(dept.id);
    this.selectedCountCache.delete(dept.id);

    const members = this.getCachedDeptMembers(dept.id);
    if (members.length) {
      this.selectedMembers = this.selectedMembers.filter(s => {
        const fromThisDept = members.some(m => this.isSameMember(m, s));
        if (!fromThisDept) { return true; }
        if (this.manualMemberIds.has(s.id)) { return true; }
        return this.isInAnySelectedDept(s);
      });
      this.rebuildSelectedMemberKeys();
    }
    this.refreshSelectedDepartments();
  }

  private mergeDeptMembers(members: MemberResult[]): void {
    const additions = members.filter(m => !this.isSelected(m));
    if (additions.length) {
      this.selectedMembers = [...this.selectedMembers, ...additions];
      this.rebuildSelectedMemberKeys();
    }
  }

  private isInAnySelectedDept(member: MemberResult): boolean {
    return Array.from(this.selectedDeptIds).some(deptId => {
      const members = this.getCachedDeptMembers(deptId);
      return members.some(m => this.isSameMember(m, member));
    });
  }

  getInitials(name: string): string {
    return (name || '')
      .split(' ')
      .filter(Boolean)
      .map(n => n[0])
      .join('')
      .slice(0, 2)
      .toUpperCase() || '?';
  }

  trackByMemberId(_index: number, member: MemberResult): string {
    return member.id || member.email;
  }

  trackByDeptId(_index: number, dept: DepartmentOption): string {
    return dept.id;
  }

  private selectionSignature(): string {
    const members = this.selectedMembers.map(m => this.workdayIdOf(m) || m.id).sort((a, b) => a.localeCompare(b)).join(',');
    // Compare by name, not id: a saved department can hold a placeholder id until it is
    // resolved, and swapping in the real id must not look like a user edit.
    const depts = this.selectedDepartments
      .map(d => this.normalizeDepartmentName(d.name))
      .sort((a, b) => a.localeCompare(b))
      .join(',');
    return `${members}#${depts}`;
  }

  private normalizeDepartmentName(name: string): string {
    return (name || '').trim().replaceAll(/\s+/g, ' ').toLowerCase();
  }

  get hasDuplicateDepartmentName(): boolean {
    if (this.isEdit) { return false; }
    const normalized = this.normalizeDepartmentName(this.customName);
    return !!normalized && this.existingDepartmentNames.has(normalized);
  }

  get hasDepartmentNameMaxLengthError(): boolean {
    return this.wasDepartmentNameTrimmed;
  }

  get canSave(): boolean {
    if (this.customName.trim().length === 0 || this.selectedMembers.length === 0) { return false; }
    if (this.customName.trim().length > this.maxDepartmentNameLength) { return false; }
    if (this.hasDuplicateDepartmentName) { return false; }
    if (this.isEdit && this.selectionSignature() === this.originalSignature && this.customName.trim() === this.originalName.trim()) { return false; }
    return true;
  }

  close(): void {
    this.dialogRef.close();
  }

  private workdayIdOf(m: MemberResult): string {
    const r = m.raw ?? {};
    const candidates = [
      r.tm_workday_id, r.user_guid, r.user_id, r.userId, r.tm_id, m.id
    ];
    const found = candidates.find(v => String(v ?? '').trim().length > 0);
    return String(found ?? '').trim();
  }

  private getExcludedMembers(): Array<{ deptId: string; workdayId: string; name: string; email: string }> {
    const excluded: Array<{ deptId: string; workdayId: string; name: string; email: string }> = [];
    this.selectedDepartments.forEach(dept => {
      const members = this.deptMemberCache.get(dept.id) ?? [];
      members.forEach(m => {
        if (this.isSelected(m)) { return; }
        excluded.push({
          deptId: dept.id,
          workdayId: String(m.raw?.tm_workday_id ?? m.raw?.user_id ?? m.raw?.userId ?? ''),
          name: m.name,
          email: m.email
        });
      });
    });
    return excluded;
  }

  private buildCustomSelectedDept(): Array<{ deptname: string; flag: boolean; selected_members: string[] }> {
    const groups: Array<{ deptname: string; flag: boolean; selected_members: string[] }> = [];
    const assigned: MemberResult[] = [];

    this.selectedDepartments.forEach(dept => {
      const members = this.getCachedDeptMembers(dept.id);
      const selected = members.filter(m => this.isSelected(m));
      if (!selected.length) { return; }
      assigned.push(...selected);
      groups.push({
        deptname: dept.name,
        flag: true,
        selected_members: selected.map(m => this.workdayIdOf(m))
      });
    });

    const loose = this.selectedMembers.filter(m => !assigned.some(a => this.isSameMember(a, m)));
    if (loose.length) {
      groups.push({
        deptname: '',
        flag: false,
        selected_members: loose.map(m => this.workdayIdOf(m))
      });
    }

    return groups;
  }

  save(): void {
    if (!this.canSave || this.isSaving) { return; }

    if (this.customName.trim().length > this.maxDepartmentNameLength) {
      this.wasDepartmentNameTrimmed = true;
      return;
    }

    const payload = {
      customDepartmentName: this.customName.trim(),
      custom_selected_dept: this.buildCustomSelectedDept(),
      excludedTeamMembers: this.getExcludedMembers()
    };

    this.isSaving = true;
    const request = { ...payload, deptId: this.deptId };
    const save$ = this.isEdit
      ? this.createProjectService.updateCustomDepartment(request)
      : this.createProjectService.saveCustomDepartment(request);
    save$.subscribe({
      next: (response: any) => {
        this.isSaving = false;
        this.dialogRef.close({
          name: payload.customDepartmentName,
          members: this.selectedMembers.map(s => s.raw),
          payload,
          response
        });
      },
      error: (error: any) => {
        this.isSaving = false;
        console.info('Save custom department failed', error);
      }
    });
  }
}